import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface RestaurantQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantName: string;
  restaurantSlug: string;
}

const RestaurantQRCodeModal: React.FC<RestaurantQRCodeModalProps> = ({
  isOpen,
  onClose,
  restaurantName,
  restaurantSlug,
}) => {
  const { t, isRTL } = useLanguage();
  const svgRef = useRef<SVGSVGElement>(null);

  if (!isOpen) return null;

  // The full URL that the QR code points to
  const menuUrl = `https://turbo.sd/menu/${restaurantSlug}`;


  const handleDownload = () => {
    if (!svgRef.current) return;

    // Get SVG string
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    
    // Create a canvas to convert SVG to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    // Scale up for high-res print quality
    const size = 1000;
    canvas.width = size;
    canvas.height = size;

    img.onload = () => {
      // Draw white background
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, size, size);
        
        // Draw the SVG image
        ctx.drawImage(img, 0, 0, size, size);

        // Download PNG
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `${restaurantSlug}-qr-code.png`;
        downloadLink.href = `${pngFile}`;
        downloadLink.click();
      }
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('restaurantQRCode') || 'Menu QR Code'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 flex flex-col items-center text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('qrCodeDescription') || 'Print and place this QR code on tables or flyers. Customers can scan it to view your menu instantly.'}
          </p>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
            <QRCodeSVG
              id="qr-code-svg"
              value={menuUrl}
              size={200}
              level="H"
              includeMargin={false}
              ref={svgRef}
              imageSettings={{
                src: "/favicon.ico", // Ensure this path works or use a data URL for Turbo Logo
                x: undefined,
                y: undefined,
                height: 40,
                width: 40,
                excavate: true,
              }}
            />
          </div>

          <h3 className="font-bold text-gray-900 dark:text-white text-xl mb-1">
            {restaurantName}
          </h3>
          
          <a
            href={menuUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 mb-8"
          >
            {menuUrl}
            <ExternalLink className="w-3 h-3" />
          </a>

          <div className="w-full flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('close')}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-[0.98]"
            >
              <Download className="w-5 h-5" />
              {t('downloadQR') || 'Download PNG'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RestaurantQRCodeModal;
